import { Signal, computed, inject, signal } from "@angular/core";
import {
  TerminologyFreshnessMetadata,
  TerminologyRuntimeContext,
  TerminologyService,
} from "@app/shared/services/terminology.service";
import { ColDef } from "ag-grid-community";

import { TerminologyUsageDeclaration } from "./terminology-usage";
import { createTerminologyRenderAttributes } from "./terminology-render-attributes";

export interface TerminologyGridHeaderParams {
  readonly template: string;
}

/**
 * Creates the reactive display and identity portion of an AG Grid column definition.
 *
 * The adapter supplies AG Grid's default header template instead of replacing its header
 * component. Sorting, filtering, menus, resize behavior, keyboard navigation, and sort indicators
 * therefore remain grid-owned. `headerName` stays plain resolved text for exports and column tools.
 */
export function createTerminologyGridHeader(
  terminologyUsage: TerminologyUsageDeclaration,
  terminologyText: Signal<string>,
): Signal<Pick<ColDef, "headerName" | "headerComponentParams">> {
  const terminology = inject(TerminologyService);
  const localeCode = terminologyUsage.localeCode ?? "en-us";
  const context = typeof terminology.contextForConfiguration === "function"
    ? terminology.contextForConfiguration(terminologyUsage.configurationCode, localeCode)
    : signal<TerminologyRuntimeContext | null>(null);
  const freshness = typeof terminology.freshnessForConfiguration === "function"
    ? terminology.freshnessForConfiguration(terminologyUsage.configurationCode, localeCode)
    : signal<TerminologyFreshnessMetadata | null>(null);

  return computed(() => ({
    headerName: terminologyText(),
    headerComponentParams: {
      template: buildHeaderTemplate(terminologyUsage, context(), freshness()),
    } satisfies TerminologyGridHeaderParams,
  }));
}

function buildHeaderTemplate(
  usage: TerminologyUsageDeclaration,
  context: TerminologyRuntimeContext | null,
  freshness: TerminologyFreshnessMetadata | null,
): string {
  const attributes = formatAttributes(
    createTerminologyRenderAttributes(usage, context, freshness),
  );

  return `<div class="ag-cell-label-container" role="presentation">
    <span data-ref="eMenu" class="ag-header-icon ag-header-cell-menu-button" aria-hidden="true"></span>
    <span data-ref="eFilterButton" class="ag-header-icon ag-header-cell-filter-button" aria-hidden="true"></span>
    <div data-ref="eLabel" class="ag-header-cell-label" role="presentation">
      <span data-ref="eText" class="ag-header-cell-text" ${attributes}></span>
      <span data-ref="eFilter" class="ag-header-icon ag-header-label-icon ag-filter-icon" aria-hidden="true"></span>
      <ag-sort-indicator data-ref="eSortIndicator"></ag-sort-indicator>
    </div>
  </div>`;
}

function formatAttributes(attributes: Readonly<Record<string, string | null | undefined>>): string {
  return Object.entries(attributes)
    .filter((entry): entry is [string, string] => entry[1] != null)
    .map(([name, value]) => `${name}="${escapeAttribute(value)}"`)
    .join(" ");
}

function escapeAttribute(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll('"', "&quot;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");
}
